import { getStrategyCategoryPageList, getStrategyDetail, getStrategyPageList } from '@/api/strategy'
import { getCaseDetail, getUserCasePageList } from '@/api/case'

export const STRATEGY_LIST_DEFAULT_TITLE = '装修攻略_自建房攻略-化化网'
export const STRATEGY_DETAIL_DEFAULT_TITLE = '装修与自建房攻略-化化网'
export const STRATEGY_DETAIL_DEFAULT_DESCRIPTION = '化化网装修攻略、自建房攻略文章详情内容，详细拆解与解读避坑干货与知识'
export const STRATEGY_DETAIL_DEFAULT_KEYWORDS = '装修攻略,自建房攻略,装修避坑,装修知识,自建房避坑,自建房知识,化化网'
export const CASE_DETAIL_DEFAULT_TITLE = '业主装修案例_自建房案例-化化网'

const strategyCategoryMap = new Map()
const strategyArticleMap = new Map()
const caseDetailMap = new Map()
let seoRoutesPromise = null

function toId(value) {
  return value === undefined || value === null ? '' : String(value)
}

function getPageData(res) {
  return res?.data || {}
}

function getPageList(res) {
  const data = getPageData(res)
  const list = data.data || data.records || data
  return Array.isArray(list) ? list : []
}

function getDetailData(res) {
  return res?.data || {}
}

function pickString(...values) {
  const value = values.find((item) => item !== undefined && item !== null && String(item).trim())
  return value === undefined ? '' : String(value).trim()
}

function decodeBasicEntities(value = '') {
  return String(value)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&ldquo;|&rdquo;/gi, '"')
    .replace(/&lsquo;|&rsquo;/gi, "'")
    .replace(/&rarr;/gi, '→')
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
}

function stripHtml(value = '') {
  return decodeBasicEntities(value)
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function truncateText(value, maxLength = 120) {
  const text = stripHtml(value)
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength).replace(/[，。；、,.!?！？\s]+$/g, '')}...`
}

function getPageTotal(res, fallback = 0) {
  const total = getPageData(res).total
  const parsed = Number(total)
  return Number.isFinite(parsed) ? parsed : fallback
}

function rememberStrategyCategory(category) {
  const id = toId(category?.id)
  if (!id) return

  strategyCategoryMap.set(id, {
    id,
    name: category?.name || '',
    childIds: Array.isArray(category?.listModel)
      ? category.listModel.map((item) => toId(item?.id)).filter(Boolean)
      : []
  })
}

function rememberStrategyArticle(article, context = {}) {
  const id = toId(article?.id)
  if (!id) return

  const previous = strategyArticleMap.get(id) || {}
  strategyArticleMap.set(id, {
    ...previous,
    id,
    title: pickString(article?.title, previous.title),
    description: pickString(article?.seoDescription, article?.description, article?.summary, previous.description),
    keywords: pickString(article?.seoKeywords, article?.keywords, article?.tagNames, previous.keywords),
    content: pickString(article?.content, previous.content),
    categoryId: pickString(article?.parentCategoryId, article?.categoryParentId, context.categoryId, previous.categoryId),
    childCategoryId: pickString(article?.childCategoryId, article?.strategyCategoryId, article?.categoryId, context.childCategoryId, previous.childCategoryId)
  })
}

function rememberCaseDetail(caseItem) {
  const id = toId(caseItem?.id)
  if (!id) return

  const previous = caseDetailMap.get(id) || {}
  caseDetailMap.set(id, {
    ...previous,
    id,
    title: caseItem?.title || previous.title || ''
  })
}

async function fetchPagedList(fetcher, params, pageSize = 100) {
  const first = await fetcher({ ...params, pageNum: 1, pageSize })
  const list = getPageList(first)
  const total = getPageTotal(first, list.length)
  const pageCount = Math.ceil(total / pageSize)

  if (pageCount <= 1) {
    return list
  }

  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) => (
      fetcher({ ...params, pageNum: index + 2, pageSize })
    ))
  )

  return rest.reduce((result, res) => result.concat(getPageList(res)), list)
}

async function runInBatches(items, mapper, batchSize = 10) {
  const results = []

  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize)
    results.push(...await Promise.all(batch.map(mapper)))
  }

  return results
}

async function fetchStrategyCategories() {
  const categories = await fetchPagedList(getStrategyCategoryPageList, {
    categoryId: 0,
    isExtension: true
  }, 100)

  categories.forEach(rememberStrategyCategory)
  return categories
}

async function collectStrategyRoutes(categories) {
  const routeSet = new Set()
  const articleIds = new Set()

  await Promise.all(categories.map(async (category) => {
    const categoryId = toId(category?.id)
    if (!categoryId) return

    const childIds = strategyCategoryMap.get(categoryId)?.childIds || []
    routeSet.add(`/strategy/list/${categoryId}`)
    routeSet.add(`/m/strategy/list/${categoryId}`)

    const articles = await fetchPagedList(getStrategyPageList, {
      categoryIdList: [categoryId, ...childIds].filter(Boolean)
    }, 100)

    articles.forEach((article) => {
      rememberStrategyArticle(article, {
        categoryId,
        childCategoryId: article?.childCategoryId || article?.strategyCategoryId || article?.categoryId
      })
      const articleId = toId(article?.id)
      if (articleId) {
        articleIds.add(articleId)
        routeSet.add(`/strategy/detail/${articleId}`)
        routeSet.add(`/m/strategy/detail/${articleId}`)
      }
    })
  }))

  await runInBatches(Array.from(articleIds), async (articleId) => {
    try {
      const detail = await getStrategyDetail(articleId)
      rememberStrategyArticle(getDetailData(detail))
    } catch (error) {
      console.warn(`Failed to fetch strategy detail for SEO: ${articleId}`, error)
    }
  })

  return Array.from(routeSet)
}

async function collectCaseRoutes() {
  const cases = await fetchPagedList(getUserCasePageList, {}, 100)
  const caseIds = []

  const routes = cases
    .flatMap((caseItem) => {
      rememberCaseDetail(caseItem)
      const caseId = toId(caseItem?.id)
      if (caseId) {
        caseIds.push(caseId)
      }
      return caseId ? [`/case/detail/${caseId}`, `/m/case/detail/${caseId}`] : []
    })
    .filter(Boolean)

  await runInBatches(caseIds, async (caseId) => {
    try {
      const detail = await getCaseDetail(caseId)
      rememberCaseDetail(getDetailData(detail))
    } catch (error) {
      console.warn(`Failed to fetch case detail for SEO: ${caseId}`, error)
    }
  })

  return routes
}

export async function collectSeoRoutes() {
  if (!seoRoutesPromise) {
    seoRoutesPromise = (async () => {
      const buildApiBaseUrl = typeof process !== 'undefined'
        ? (process.env?.VUE_APP_BASE_URL || import.meta.env.VUE_APP_BASE_URL)
        : import.meta.env.VUE_APP_BASE_URL

      console.log(`[seo] mode=${import.meta.env.MODE || 'unknown'} api=${buildApiBaseUrl || 'https://api.huahuaeco.com'}`)

      const categories = await fetchStrategyCategories()
      const [strategyRoutes, caseRoutes] = await Promise.all([
        collectStrategyRoutes(categories),
        collectCaseRoutes()
      ])

      console.log(`[seo] strategyRoutes=${strategyRoutes.length} caseRoutes=${caseRoutes.length}`)

      return Array.from(new Set([
        ...strategyRoutes,
        ...caseRoutes
      ]))
    })()
  }

  return seoRoutesPromise
}

export function getStrategyCategoryTitle(categoryId) {
  return strategyCategoryMap.get(toId(categoryId))?.name || ''
}

export function getStrategyCategoryChildIds(categoryId) {
  return strategyCategoryMap.get(toId(categoryId))?.childIds || []
}

export function getStrategyListSeoTitle(categoryId) {
  const categoryTitle = getStrategyCategoryTitle(categoryId)
  return categoryTitle
    ? `${categoryTitle} - 装修攻略_自建房攻略-化化网`
    : STRATEGY_LIST_DEFAULT_TITLE
}

export function getStrategyDetailSeoTitle(strategyId) {
  return getStrategyDetailSeoHead(strategyId).title
}

export function buildStrategyDetailSeoHead(article) {
  const title = pickString(article?.title)
  const sourceDescription = pickString(article?.description, article?.summary, article?.content)
  const description = truncateText(sourceDescription || title, 120) || STRATEGY_DETAIL_DEFAULT_DESCRIPTION

  const titleKeywords = stripHtml(title)
    .split(/[｜|_\-—,，.。/、:：?？!！\s]+/)
    .map((item) => item.trim())
    .filter((item) => item.length >= 2)
  const keywords = Array.from(new Set([
    ...pickString(article?.keywords).split(/[,，、\s]+/).filter(Boolean),
    ...titleKeywords,
    '装修攻略',
    '自建房攻略',
    '装修避坑',
    '化化网'
  ])).slice(0, 12).join(',') || STRATEGY_DETAIL_DEFAULT_KEYWORDS

  return {
    title: title ? `${title} -装修与自建房攻略-化化网` : STRATEGY_DETAIL_DEFAULT_TITLE,
    description,
    keywords
  }
}

export function getStrategyDetailSeoHead(strategyId) {
  return buildStrategyDetailSeoHead(strategyArticleMap.get(toId(strategyId)))
}

export function getStrategyArticleRouteMeta(strategyId) {
  const article = strategyArticleMap.get(toId(strategyId)) || {}
  return {
    categoryId: article.categoryId || '',
    childCategoryId: article.childCategoryId || ''
  }
}

export function getCaseDetailSeoTitle(caseId) {
  const title = caseDetailMap.get(toId(caseId))?.title || ''
  return title
    ? `${title} - 业主装修案例_自建房案例-化化网`
    : CASE_DETAIL_DEFAULT_TITLE
}
